import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SITE_BASE_URL } from '../../../constants';
import { ROUTES } from '../../../routes';
import { PageMeta } from '../../seo/PageMeta';
import { JsonLd } from '../../seo/JsonLd';
import Button from '@ui/components/Button/Button';
import { useAuth } from '@breath-lab/state';
import { logEvent } from '../../../infra/firebase/analytics';
import { AnalyticsEvent, AnalyticsParam } from '../../../domain/analytics';
import styles from './PricingPage.module.css';

const TRIAL_DAYS = 7;
const PRICE_MONTHLY_USD = '5.99';
const PRICE_ANNUAL_USD = '34.99';
const CURRENCY = 'USD';

export default function PricingPage() {
  const { t } = useTranslation('pricing');
  const { openLoginModal } = useAuth();

  useEffect(() => {
    logEvent(AnalyticsEvent.screenView, { [AnalyticsParam.screenName]: 'pricing' });
  }, []);

  const productJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Breath Lab Premium',
    description: t('subtitle', { trialDays: TRIAL_DAYS }),
    image: [`${SITE_BASE_URL}/og-image.png`],
    applicationCategory: 'HealthApplication',
    operatingSystem: 'Web, iOS, Android',
    brand: { '@type': 'Brand', name: 'Breath Lab' },
    offers: [
      {
        '@type': 'Offer',
        name: 'Breath Lab Premium — Monthly',
        price: PRICE_MONTHLY_USD,
        priceCurrency: CURRENCY,
        url: `${SITE_BASE_URL}${ROUTES.pricing}`,
        availability: 'https://schema.org/InStock',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: PRICE_MONTHLY_USD,
          priceCurrency: CURRENCY,
          unitText: 'MONTH',
        },
      },
      {
        '@type': 'Offer',
        name: 'Breath Lab Premium — Annual',
        price: PRICE_ANNUAL_USD,
        priceCurrency: CURRENCY,
        url: `${SITE_BASE_URL}${ROUTES.pricing}`,
        availability: 'https://schema.org/InStock',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: PRICE_ANNUAL_USD,
          priceCurrency: CURRENCY,
          unitText: 'ANNUAL',
        },
      },
    ],
  };

  const faqItems = t('faq.items', { returnObjects: true, trialDays: TRIAL_DAYS }) as Array<{
    q: string;
    a: string;
  }>;

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  const includesItems = t('includes.items', { returnObjects: true }) as string[];

  return (
    <>
      <PageMeta
        title={t('title')}
        description={t('subtitle', { trialDays: TRIAL_DAYS })}
        path={ROUTES.pricing}
      />
      <JsonLd data={productJsonLd} />
      <JsonLd data={faqJsonLd} />

      <main className={styles.page}>
        <header className={styles.hero}>
          <h1 className={styles.title}>{t('title')}</h1>
          <p className={styles.subtitle}>{t('subtitle', { trialDays: TRIAL_DAYS })}</p>
        </header>

        <section className={styles.plans}>
          <article className={`${styles.plan} ${styles.planFeatured}`}>
            <div className={styles.planBadge}>{t('plans.annual.badge')}</div>
            <h2 className={styles.planName}>{t('plans.annual.name')}</h2>
            <p className={styles.planPrice}>{t('plans.annual.price')}</p>
            <p className={styles.planPerMonth}>{t('plans.annual.perMonth')}</p>
          </article>
          <article className={styles.plan}>
            <h2 className={styles.planName}>{t('plans.monthly.name')}</h2>
            <p className={styles.planPrice}>{t('plans.monthly.price')}</p>
            <p className={styles.planPerMonth}>{t('plans.monthly.perMonth')}</p>
          </article>
        </section>

        <p className={styles.regionNote}>{t('regionNote')}</p>

        <section className={styles.includes}>
          <h2 className={styles.includesTitle}>{t('includes.title')}</h2>
          <ul className={styles.includesList}>
            {includesItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <section className={styles.ctaBlock}>
          <h2 className={styles.ctaTitle}>{t('trial.headline', { trialDays: TRIAL_DAYS })}</h2>
          <p className={styles.ctaBody}>{t('trial.body', { trialDays: TRIAL_DAYS })}</p>
          <div className={styles.ctaButtons}>
            <Button as={Link} to={ROUTES.signup} variant="primary" size="lg">
              {t('ctaPrimary')}
            </Button>
            <Button variant="ghost" size="md" onClick={() => openLoginModal(ROUTES.pricing)}>
              {t('ctaSecondary')}
            </Button>
          </div>
        </section>

        <section className={styles.faq}>
          <h2 className={styles.faqTitle}>{t('faq.title')}</h2>
          <dl>
            {faqItems.map((item) => (
              <div key={item.q} className={styles.faqItem}>
                <dt className={styles.faqQuestion}>{item.q}</dt>
                <dd className={styles.faqAnswer}>{item.a}</dd>
              </div>
            ))}
          </dl>
        </section>
      </main>
    </>
  );
}
